import React, { useMemo } from 'react';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import styled from 'styled-components';
import {
  HighlightedEntity,
  withHighlightingContext,
} from '../../HighlightingContext';
import { AliasHighlight } from '../AliasNavigation/AliasHighlight';
import { ICohortRow, IRowMeta } from '../../../redux/slices/oldBuilder';
import {
  eventConditionsPrimitiveSelector,
  measuresSelector,
  rowSelector,
} from '../../../redux/selectors/builder';
import { useAppSelector } from '../../../redux/hooks';
import { CohortRow } from '../Cohorts';
import BuilderMeasure from './BuilderMeasure';
import DndDroppableZone from '../../DndEntity/DndDroppableZone';
import { EntityDropzoneType } from '../../QuickAccessDictionary/DictionaryTabs/types';
import { useDndEntityInternalContext } from '../../DndEntity';
import { isEmptyEventConditions } from '../../../utils/builder';
import {
  createDictionaryEntityDropzoneId,
  getDndActiveItemData,
} from '../../DndEntity/utils';
import {
  EntityDropzoneDataTypeEnum,
  IEventEntityDropzoneDataType,
  IMeasureEntityDropzoneDataType,
} from '../../DndEntity/validation';
import { MAX_EVENT_CONDITIONS_COUNT } from '../../../config/constants';
import { getCharacterByIncrementedIndex } from '../../../utils/character';
import { borderedContent } from '../../../styles/shareableStyles';
import { theme } from '../../../styles/theme';

interface IProps {
  rowMeta: IRowMeta;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  aliasId?: string;
  hideContent?: boolean;
}

const Container = styled(HighlightedEntity)`
  ${borderedContent};
`;

const CohortPart = styled.div`
  padding-right: 16px;
  padding-left: 16px;
`;

const MeasureCohortsWrapper = styled.div`
  padding: 12px 0;
  border-top: 1px solid ${theme.colors.border};
`;

const MeasureCohortRow = styled(CohortRow)`
  padding: 8px 16px;
`;

const MeasureRow = ({
  rowMeta,
  aliasId,
  dragHandleProps,
  hideContent,
}: IProps) => {
  const eventConditionsSelector = useMemo(eventConditionsPrimitiveSelector, []);
  const eventConditions = useAppSelector((state) =>
    eventConditionsSelector(state, rowMeta),
  );
  const measures = useAppSelector(measuresSelector);

  const cohortRowMeta: IRowMeta = {
    ...rowMeta,
    cohortRowType: 'cohortFilter',
  };

  const cohortRow: ICohortRow = useAppSelector((state) =>
    rowSelector(state, cohortRowMeta),
  );

  const { activeItem } = useDndEntityInternalContext();

  const newEventsDropzoneId = useMemo(
    () => createDictionaryEntityDropzoneId(EntityDropzoneType.Events, rowMeta),
    [rowMeta],
  );
  const newMeasureDropzoneId = useMemo(
    () =>
      createDictionaryEntityDropzoneId(EntityDropzoneType.Measures, rowMeta),
    [rowMeta],
  );

  const dropzoneData = useMemo(() => {
    const data = getDndActiveItemData(activeItem);

    if (data) {
      if (
        data.__typename === 'TagLeafSimpleMeasure' ||
        data.__typename === 'TagLeafCompoundMeasure'
      ) {
        return {
          id: newMeasureDropzoneId,
          rowMeta,
          type: EntityDropzoneDataTypeEnum.Measures,
        } satisfies IMeasureEntityDropzoneDataType;
      }

      if (
        data.__typename === 'TagLeafBasicEvent' ||
        data.__typename === 'TagLeafVirtualEvent'
      ) {
        const areEventConditionsEmpty = isEmptyEventConditions(eventConditions);

        return {
          type: EntityDropzoneDataTypeEnum.Events,
          rowMeta: {
            ...rowMeta,
            eventIndex: areEventConditionsEmpty ? 0 : eventConditions.length,
          },
          isNewEvent: !areEventConditionsEmpty,
          shouldAddMeasureFunction: areEventConditionsEmpty,
          id: newEventsDropzoneId,
        } satisfies IEventEntityDropzoneDataType;
      }
    }
  }, [
    activeItem,
    rowMeta,
    eventConditions,
    newEventsDropzoneId,
    newMeasureDropzoneId,
  ]);

  const getCustomMessage = () => {
    const measureCharacter = getCharacterByIncrementedIndex(
      'A',
      rowMeta.rowIndex,
    );

    if (dropzoneData?.type === EntityDropzoneDataTypeEnum.Events) {
      if (eventConditions.length >= MAX_EVENT_CONDITIONS_COUNT) {
        return 'Limit of 5 Events reached';
      }
      return `+ Add Event to Measure ${measureCharacter}`;
    }

    if (dropzoneData?.type === EntityDropzoneDataTypeEnum.Measures) {
      if (!isEmptyEventConditions(eventConditions)) {
        return `Replace Measure ${measureCharacter} with New Measure`;
      }
      return `+ Add Measure to Measure ${measureCharacter}`;
    }
  };

  const checkHasErrors = () => {
    const data = getDndActiveItemData(activeItem);

    if (
      data?.__typename === 'TagLeafBasicEvent' ||
      data?.__typename === 'TagLeafVirtualEvent'
    ) {
      return eventConditions.length >= MAX_EVENT_CONDITIONS_COUNT;
    }

    if (data?.__typename === 'TagLeafCompoundMeasure') {
      return measures.some(
        (measure) => !isEmptyEventConditions(measure.eventConditions),
      );
    }

    return false;
  };

  return (
    <AliasHighlight aliasId={aliasId}>
      <Container>
        <DndDroppableZone
          accepts={[EntityDropzoneType.Measures, EntityDropzoneType.Events]}
          id={newEventsDropzoneId}
          data={dropzoneData}
          customMessage={getCustomMessage()}
          hasError={checkHasErrors()}
        >
          <BuilderMeasure
            rowMeta={rowMeta}
            cohortRowMeta={cohortRowMeta}
            hideContent={hideContent}
            dragHandleProps={dragHandleProps}
          />
        </DndDroppableZone>
        {cohortRow?.isActive && (
          <CohortPart>
            <MeasureCohortsWrapper>
              <MeasureCohortRow
                rowMeta={cohortRowMeta}
                cohortRow={cohortRow}
                allowRemove
              />
            </MeasureCohortsWrapper>
          </CohortPart>
        )}
      </Container>
    </AliasHighlight>
  );
};

export default withHighlightingContext(MeasureRow);
